import { useEffect, useState } from "react";
import { Star, LogOut, RefreshCw, CalendarCheck, BadgePercent, MessageSquare, GraduationCap, CreditCard, Mail, Trash2, TicketPercent, Table2, LayoutGrid } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CardModal } from "@/components/admin/CardModal";
import { ChargeModal } from "@/components/admin/ChargeModal";
import { RefundModal } from "@/components/admin/RefundModal";

const normPhone = (p) => (p || "").replace(/\D/g, "").slice(-10);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-brand-navy placeholder:text-slate-400 focus:border-brand-blue focus:outline-none";

const TIME_ORDER = ["8–10 AM", "10 AM–12 PM", "12–2 PM", "2–4 PM", "4–6 PM"];

const fmtPickupDay = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const sheetBadge = (st) =>
  ({
    new: "bg-fall-bg text-fall-orange border-fall-border",
    scheduled: "bg-brand-blue-light text-brand-blue border-brand-blue/30",
    done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  })[st] || "bg-slate-100 text-slate-500 border-slate-200";

// Spreadsheet view of every pickup, arranged by pickup date (soonest first).
const BookingsSheet = ({ bookings, cards, voucherClaims }) => {
  const rows = [...(bookings || [])].sort((a, b) => {
    const d = (a.pickup_date || "").localeCompare(b.pickup_date || "");
    if (d !== 0) return d;
    return TIME_ORDER.indexOf(a.time_window) - TIME_ORDER.indexOf(b.time_window);
  });
  if (!rows.length) return <p className="p-6 text-sm text-slate-500">No pickup requests yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
            <th className="px-4 py-3">Pickup date</th>
            <th className="px-4 py-3">Time</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Address</th>
            <th className="px-4 py-3">Services</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Card</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => {
            const st = b.status || "new";
            const card = cards?.[normPhone(b.phone)];
            const hasVoucher = (voucherClaims || []).some((v) => normPhone(v.phone) === normPhone(b.phone));
            return (
              <tr key={b.id} data-testid={`sheet-row-${b.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                <td className="whitespace-nowrap px-4 py-3 font-bold text-brand-navy">{fmtPickupDay(b.pickup_date)}</td>
                <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-600">{b.time_window}</td>
                <td className="px-4 py-3">
                  <p className="font-bold text-brand-navy">
                    {b.name}
                    {hasVoucher && <span className="ml-1.5 rounded-full bg-fall-amber/15 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-fall-orange">$10 voucher</span>}
                  </p>
                  <a className="text-xs font-semibold text-brand-blue" href={`tel:${b.phone}`}>{b.phone}</a>
                </td>
                <td className="px-4 py-3 text-slate-600">{b.address}, {b.city}</td>
                <td className="max-w-[220px] px-4 py-3 text-slate-600">
                  {b.services.join(", ")}
                  {b.comforters > 0 && <span className="ml-1.5 whitespace-nowrap rounded-full bg-fall-bg px-1.5 py-0.5 text-[10px] font-bold text-fall-orange">{b.comforters} comf.</span>}
                </td>
                <td className="px-4 py-3">
                  <span data-testid={`sheet-status-${b.id}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest ${sheetBadge(st)}`}>{st}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs font-bold">
                  {card ? <span className="text-emerald-700">{card.brand} •••• {card.last4}</span> : <span className="text-slate-400">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState("oxfordcleanersaz@gmail.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardBooking, setCardBooking] = useState(null);
  const [chargeBooking, setChargeBooking] = useState(null);
  const [refundPayment, setRefundPayment] = useState(null);
  const [sendingLink, setSendingLink] = useState(null);
  const [bookingView, setBookingView] = useState("sheet"); // "sheet" | "cards"
  const [dlName, setDlName] = useState("");
  const [dlPhone, setDlPhone] = useState("");
  const [dlEmail, setDlEmail] = useState("");
  const [dlSending, setDlSending] = useState(false);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/overview", { withCredentials: true });
      setData(data);
      setAuthed(true);
    } catch {
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/login", { email, password }, { withCredentials: true });
      await loadOverview();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/admin/logout", {}, { withCredentials: true }).catch(() => {});
    setAuthed(false);
    setData(null);
    setPassword("");
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status }, { withCredentials: true });
      setData((prev) => ({
        ...prev,
        bookings: prev.bookings.map((b) => (b.id === id ? { ...b, status } : b)),
      }));
      toast.success(`Pickup marked as ${status}`);
    } catch {
      toast.error("Couldn't update the status — try again.");
    }
  };

  const onCardSaved = (booking, card) => {
    setData((prev) => ({
      ...prev,
      cards: { ...(prev?.cards || {}), [normPhone(booking.phone)]: card },
    }));
  };

  const sendCardLink = async (booking) => {
    setSendingLink(booking.id);
    try {
      const { data: r } = await api.post("/admin/cards/email-link", { booking_id: booking.id }, { withCredentials: true });
      toast.success(`Secure card link emailed to ${r.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the link — try again.");
    } finally {
      setSendingLink(null);
    }
  };

  const sendDirectLink = async (e) => {
    e.preventDefault();
    setDlSending(true);
    try {
      const { data: r } = await api.post(
        "/admin/cards/email-link-direct",
        { name: dlName, phone: dlPhone, email: dlEmail },
        { withCredentials: true }
      );
      toast.success(`Secure card link emailed to ${r.sent_to}`);
      setDlName(""); setDlPhone(""); setDlEmail("");
      await loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the link — try again.");
    } finally {
      setDlSending(false);
    }
  };

  const resendCardLink = async (person) => {
    setDlSending(true);
    try {
      const { data: r } = await api.post(
        "/admin/cards/email-link-direct",
        { name: person.name, phone: person.phone, email: person.email },
        { withCredentials: true }
      );
      toast.success(`New link emailed to ${r.sent_to}`);
      await loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't resend — try again.");
    } finally {
      setDlSending(false);
    }
  };

  const deleteVoucher = async (v) => {
    if (!window.confirm(`Delete the $10 voucher for ${v.name}?`)) return;
    try {
      await api.delete(`/admin/vouchers/${v.id}`, { withCredentials: true });
      setData((prev) => ({ ...prev, voucher_claims: prev.voucher_claims.filter((x) => x.id !== v.id) }));
      toast.success("Voucher deleted");
    } catch {
      toast.error("Couldn't delete — try again.");
    }
  };

  const deleteBooking = async (booking) => {
    if (!window.confirm(`Delete the pickup for ${booking.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/bookings/${booking.id}`, { withCredentials: true });
      setData((prev) => ({ ...prev, bookings: prev.bookings.filter((x) => x.id !== booking.id) }));
      toast.success("Pickup deleted");
    } catch {
      toast.error("Couldn't delete — try again.");
    }
  };

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-navy text-white">
        <p className="text-sm font-semibold text-slate-300">Checking session…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-brand-navy px-4">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur"
        >
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-fall-amber">
              <Star className="h-5 w-5 fill-brand-navy text-brand-navy" />
            </span>
            <div>
              <p className="font-display text-lg font-extrabold text-white">Oxford Cleaners</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">Owner sign-in</p>
            </div>
          </div>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className={`${inputCls} mt-6`}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className={`${inputCls} mt-3`}
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-rose-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            className="mt-5 w-full rounded-xl bg-fall-amber px-5 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-brand-navy transition-transform hover:scale-[1.02]"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const counts = data?.counts || { bookings: 0, claims: 0, contacts: 0 };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-brand-navy/90 text-white backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-fall-amber">
              <Star className="h-5 w-5 fill-brand-navy text-brand-navy" />
            </span>
            <p className="font-display font-extrabold">Owner Dashboard</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={loadOverview}
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold hover:border-fall-amber"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {[
            { label: "Pickup requests", value: counts.bookings, icon: CalendarCheck, testid: "admin-count-bookings" },
            { label: "Fall Special claims", value: counts.claims, icon: BadgePercent, testid: "admin-count-claims" },
            { label: "$10 vouchers", value: counts.voucher_claims ?? 0, icon: TicketPercent, testid: "admin-count-vouchers" },
            { label: "Honor claims (free)", value: counts.honor_claims ?? 0, icon: Star, testid: "admin-count-honor" },
            { label: "School claims (free)", value: counts.school_claims ?? 0, icon: GraduationCap, testid: "admin-count-school" },
            { label: "Messages", value: counts.contacts, icon: MessageSquare, testid: "admin-count-contacts" },
          ].map((c) => (
            <div key={c.label} data-testid={c.testid} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{c.label}</p>
                <c.icon className="h-5 w-5 text-fall-orange" />
              </div>
              <p className="mt-2 font-display text-4xl font-extrabold text-brand-navy">{c.value}</p>
            </div>
          ))}
        </div>

        <section className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl font-bold text-brand-navy">Pickup requests</h2>
            <div data-testid="admin-bookings-view-toggle" className="flex rounded-full border border-slate-200 bg-white p-1 text-xs font-bold">
              <button
                data-testid="admin-view-sheet"
                onClick={() => setBookingView("sheet")}
                className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors ${bookingView === "sheet" ? "bg-brand-navy text-white" : "text-slate-500 hover:text-brand-navy"}`}
              >
                <Table2 className="h-3.5 w-3.5" /> Spreadsheet
              </button>
              <button
                data-testid="admin-view-cards"
                onClick={() => setBookingView("cards")}
                className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors ${bookingView === "cards" ? "bg-brand-navy text-white" : "text-slate-500 hover:text-brand-navy"}`}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Cards
              </button>
            </div>
          </div>
          {bookingView === "sheet" ? (
            <div data-testid="admin-bookings-sheet" className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white">
              <BookingsSheet bookings={data?.bookings} cards={data?.cards} voucherClaims={data?.voucher_claims} />
            </div>
          ) : (
          <div data-testid="admin-bookings-table" className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white">
            {data?.bookings?.length ? (
              data.bookings.map((b) => {
                const st = b.status || "new";
                const badge = {
                  new: "bg-fall-bg text-fall-orange border-fall-border",
                  scheduled: "bg-brand-blue-light text-brand-blue border-brand-blue/30",
                  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
                }[st];
                const card = data?.cards?.[normPhone(b.phone)];
                const hasVoucher = (data?.voucher_claims || []).some((v) => normPhone(v.phone) === normPhone(b.phone));
                const bookingPayments = (data?.payments || []).filter((p) => p.booking_id === b.id);
                return (
                <div key={b.id} className="grid gap-1 border-b border-slate-100 p-5 last:border-0 sm:grid-cols-[1fr_auto]">
                  <div>
                    <p className="flex flex-wrap items-center gap-2 font-bold text-brand-navy">
                      {b.name} · <a className="text-brand-blue" href={`tel:${b.phone}`}>{b.phone}</a>
                      <span data-testid={`booking-status-${b.id}`} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest ${badge}`}>{st}</span>
                      {hasVoucher && (
                        <span data-testid={`booking-voucher-${b.id}`} className="flex items-center gap-1 rounded-full border border-fall-amber bg-fall-amber/15 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest text-fall-orange">
                          <TicketPercent className="h-3 w-3" /> $10 voucher — deduct at charge
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-slate-600">{b.address}, {b.city}</p>
                    <p className="mt-1 text-sm font-semibold text-slate-700">
                      {b.pickup_date} · {b.time_window} · {b.services.join(", ")}
                      {b.comforters > 0 && <span className="ml-2 rounded-full bg-fall-bg px-2 py-0.5 text-xs font-bold text-fall-orange">{b.comforters} comforter{b.comforters > 1 ? "s" : ""}</span>}
                    </p>
                    {b.notes && <p className="mt-1 text-sm text-slate-500">Notes: {b.notes}</p>}
                    <p data-testid={`booking-card-${b.id}`} className="mt-2 flex items-center gap-1.5 text-sm font-bold">
                      <CreditCard className={`h-4 w-4 ${card ? "text-emerald-600" : "text-slate-300"}`} />
                      {card ? (
                        <span className="text-emerald-700">{card.brand} •••• {card.last4} · exp {card.exp_month}/{card.exp_year}</span>
                      ) : (
                        <span className="text-slate-400">No card on file</span>
                      )}
                      {b.review_email_sent && (
                        <span data-testid={`booking-review-asked-${b.id}`} className="ml-2 flex items-center gap-1 text-xs font-bold text-amber-600">
                          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> Review asked
                        </span>
                      )}
                    </p>
                    {bookingPayments.length > 0 && (
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {bookingPayments.map((p) => (
                          <span key={p.id} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                            {p.status === "succeeded" && `Charged $${p.amount.toFixed(2)} ✓`}
                            {p.status === "failed" && `Charge failed ($${p.amount.toFixed(2)})`}
                            {p.status === "refunded" && `Refunded $${p.amount.toFixed(2)}`}
                            {p.status === "partially_refunded" && `Charged $${p.amount.toFixed(2)} · $${(p.refunded_amount || 0).toFixed(2)} refunded`}
                            {(p.status === "succeeded" || p.status === "partially_refunded") && p.stripe_payment_intent_id && (
                              <button
                                data-testid={`payment-refund-${p.id}`}
                                onClick={() => setRefundPayment(p)}
                                className="rounded-full border border-rose-300 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-rose-600 transition-colors hover:bg-rose-600 hover:text-white"
                              >
                                Refund
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {st === "new" && (
                        <button data-testid={`booking-mark-scheduled-${b.id}`} onClick={() => setStatus(b.id, "scheduled")} className="rounded-full bg-brand-blue px-4 py-1.5 text-xs font-bold text-white transition-transform hover:scale-105">Mark scheduled</button>
                      )}
                      {st === "scheduled" && (
                        <button data-testid={`booking-mark-done-${b.id}`} onClick={() => setStatus(b.id, "done")} className="rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white transition-transform hover:scale-105">Mark done</button>
                      )}
                      {st === "done" && (
                        <button data-testid={`booking-reopen-${b.id}`} onClick={() => setStatus(b.id, "new")} className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-bold text-slate-500 transition-colors hover:border-brand-blue hover:text-brand-blue">Reopen</button>
                      )}
                      <button data-testid={`booking-card-update-${b.id}`} onClick={() => setCardBooking(b)} className="rounded-full border border-brand-navy/30 px-4 py-1.5 text-xs font-bold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white">
                        {card ? "Update card" : "Add card"}
                      </button>
                      {b.email && (
                        <button data-testid={`booking-card-email-${b.id}`} onClick={() => sendCardLink(b)} disabled={sendingLink === b.id} className="flex items-center gap-1 rounded-full border border-brand-blue/40 px-4 py-1.5 text-xs font-bold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white disabled:opacity-50">
                          <Mail className="h-3 w-3" /> {sendingLink === b.id ? "Sending…" : "Email card link"}
                        </button>
                      )}
                      {card && (
                        <button data-testid={`booking-charge-${b.id}`} onClick={() => setChargeBooking(b)} className="rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white transition-transform hover:scale-105">
                          Charge card
                        </button>
                      )}
                      <button data-testid={`booking-delete-${b.id}`} onClick={() => deleteBooking(b)} className="flex items-center gap-1 rounded-full border border-rose-200 px-4 py-1.5 text-xs font-bold text-rose-500 transition-colors hover:bg-rose-600 hover:text-white">
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>
                  </div>
                  <p className="text-xs font-semibold text-slate-400">{fmtDate(b.created_at)}</p>
                </div>
                );
              })
            ) : (
              <p className="p-6 text-sm text-slate-500">No pickup requests yet.</p>
            )}
          </div>
          )}
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-bold text-brand-navy">Send a card link</h2>
          <p className="mt-1 text-sm text-slate-500">
            For a customer who couldn&apos;t book online — enter their details and we&apos;ll email a secure link
            so they can add their card (72-hour link).
          </p>
          <form
            data-testid="admin-cardlink-form"
            onSubmit={sendDirectLink}
            className="mt-4 grid gap-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-center"
          >
            <input data-testid="admin-cardlink-name-input" required minLength={2} value={dlName} onChange={(e) => setDlName(e.target.value)} placeholder="Customer name" className={inputCls} />
            <input data-testid="admin-cardlink-phone-input" required minLength={7} value={dlPhone} onChange={(e) => setDlPhone(e.target.value)} placeholder="Phone number" className={inputCls} />
            <input data-testid="admin-cardlink-email-input" required type="email" value={dlEmail} onChange={(e) => setDlEmail(e.target.value)} placeholder="Customer email" className={inputCls} />
            <button data-testid="admin-cardlink-send-button" type="submit" disabled={dlSending} className="flex items-center justify-center gap-1.5 rounded-xl bg-brand-blue px-5 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
              <Mail className="h-4 w-4" /> {dlSending ? "Sending…" : "Email card link"}
            </button>
          </form>
          <div data-testid="admin-card-links-table" className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white">
            {data?.card_links?.length ? (
              data.card_links.map((l) => {
                const used = !!l.used_at;
                const expired = !used && new Date(l.expires_at) < new Date();
                return (
                  <div key={l.id} data-testid={`cardlink-row-${l.id}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5 last:border-0">
                    <div>
                      <p className="font-bold text-brand-navy">
                        {l.name} · <a className="text-brand-blue" href={`tel:${l.phone}`}>{l.phone}</a>
                      </p>
                      <p className="text-sm text-slate-500">{l.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {used ? (
                        <span data-testid={`cardlink-status-${l.id}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">Card saved ✓</span>
                      ) : expired ? (
                        <span data-testid={`cardlink-status-${l.id}`} className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Expired</span>
                      ) : (
                        <span data-testid={`cardlink-status-${l.id}`} className="rounded-full border border-fall-border bg-fall-bg px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-fall-orange">Waiting for customer</span>
                      )}
                      {!used && (
                        <button data-testid={`cardlink-resend-${l.id}`} onClick={() => resendCardLink(l)} disabled={dlSending} className="flex items-center gap-1 rounded-full border border-brand-blue/40 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-brand-blue transition-colors hover:bg-brand-blue hover:text-white disabled:opacity-50">
                          <Mail className="h-3 w-3" /> Resend
                        </button>
                      )}
                      <p className="text-xs font-semibold text-slate-400">{fmtDate(l.created_at)}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="p-6 text-sm text-slate-500">No card links sent yet.</p>
            )}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-bold text-brand-navy">Fall Special claims</h2>
          <div data-testid="admin-claims-table" className="mt-4 overflow-hidden rounded-3xl border border-fall-border bg-fall-bg">
            {data?.claims?.length ? (
              data.claims.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 border-b border-fall-border/60 p-5 last:border-0">
                  <div>
                    <p className="font-bold text-brand-navy">
                      {c.name} · <a className="text-brand-blue" href={`tel:${c.phone}`}>{c.phone}</a>
                    </p>
                    {c.email && <p className="text-sm text-slate-500">{c.email}</p>}
                    <p className="text-sm font-semibold text-fall-orange">
                      {c.comforters} comforter{c.comforters > 1 ? "s" : ""} — {c.comforters >= 2 ? "$19.99 each" : "$25"}
                    </p>
                  </div>
                  <p className="text-xs font-semibold text-slate-400">{fmtDate(c.created_at)}</p>
                </div>
              ))
            ) : (
              <p className="p-6 text-sm text-slate-500">No Fall Special claims yet.</p>
            )}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-bold text-brand-navy">$10 new-customer vouchers</h2>
          <div data-testid="admin-vouchers-table" className="mt-4 overflow-hidden rounded-3xl border border-fall-amber/40 bg-fall-amber/5">
            {data?.voucher_claims?.length ? (
              data.voucher_claims.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-3 border-b border-fall-amber/20 p-5 last:border-0">
                  <div>
                    <p className="font-bold text-brand-navy">
                      {v.name} · <a className="text-brand-blue" href={`tel:${v.phone}`}>{v.phone}</a>
                    </p>
                    <p className="text-sm font-semibold text-fall-orange">$10 off first order — deduct when charging</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-xs font-semibold text-slate-400">{fmtDate(v.created_at)}</p>
                    <button data-testid={`voucher-delete-${v.id}`} onClick={() => deleteVoucher(v)} className="flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-rose-500 transition-colors hover:bg-rose-600 hover:text-white">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="p-6 text-sm text-slate-500">No vouchers claimed yet.</p>
            )}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-bold text-brand-navy">Honor claims — free flag &amp; uniform cleaning</h2>
          <div data-testid="admin-honor-table" className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white">
            {data?.honor_claims?.length ? (
              data.honor_claims.map((h) => (
                <div key={h.id} className="flex items-center justify-between gap-3 border-b border-slate-100 p-5 last:border-0">
                  <div>
                    <p className="font-bold text-brand-navy">
                      {h.name} · <a className="text-brand-blue" href={`tel:${h.phone}`}>{h.phone}</a>
                    </p>
                    <p className="text-sm font-semibold text-brand-blue">{h.item} — free cleaning</p>
                  </div>
                  <p className="text-xs font-semibold text-slate-400">{fmtDate(h.created_at)}</p>
                </div>
              ))
            ) : (
              <p className="p-6 text-sm text-slate-500">No honor claims yet.</p>
            )}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-bold text-brand-navy">School Spirit claims — free uniform cleaning</h2>
          <div data-testid="admin-school-table" className="mt-4 overflow-hidden rounded-3xl border border-brand-blue/20 bg-brand-blue-light/50">
            {data?.school_claims?.length ? (
              data.school_claims.map((sc) => (
                <div key={sc.id} className="flex items-center justify-between gap-3 border-b border-brand-blue/10 p-5 last:border-0">
                  <div>
                    <p className="font-bold text-brand-navy">
                      {sc.name} · <a className="text-brand-blue" href={`tel:${sc.phone}`}>{sc.phone}</a>
                    </p>
                    <p className="text-sm font-semibold text-brand-blue">
                      {sc.activity}{sc.school ? ` · ${sc.school}` : ""} — 1 uniform free
                    </p>
                  </div>
                  <p className="text-xs font-semibold text-slate-400">{fmtDate(sc.created_at)}</p>
                </div>
              ))
            ) : (
              <p className="p-6 text-sm text-slate-500">No school claims yet.</p>
            )}
          </div>
        </section>

        <section className="mt-10 mb-16">
          <h2 className="font-display text-xl font-bold text-brand-navy">Website messages</h2>
          <div data-testid="admin-contacts-table" className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white">
            {data?.contacts?.length ? (
              data.contacts.map((m) => (
                <div key={m.id} className="border-b border-slate-100 p-5 last:border-0">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-brand-navy">{m.name}{m.phone && <> · <a className="text-brand-blue" href={`tel:${m.phone}`}>{m.phone}</a></>}</p>
                    <p className="text-xs font-semibold text-slate-400">{fmtDate(m.created_at)}</p>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{m.message}</p>
                </div>
              ))
            ) : (
              <p className="p-6 text-sm text-slate-500">No messages yet.</p>
            )}
          </div>
        </section>
      </main>

      {cardBooking && (
        <CardModal
          booking={cardBooking}
          onSaved={(card) => onCardSaved(cardBooking, card)}
          onClose={() => setCardBooking(null)}
        />
      )}
      {chargeBooking && data?.cards?.[normPhone(chargeBooking.phone)] && (
        <ChargeModal
          booking={chargeBooking}
          card={data.cards[normPhone(chargeBooking.phone)]}
          onCharged={loadOverview}
          onClose={() => setChargeBooking(null)}
        />
      )}
      {refundPayment && (
        <RefundModal
          payment={refundPayment}
          onRefunded={loadOverview}
          onClose={() => setRefundPayment(null)}
        />
      )}
    </div>
  );
}
